import isNil from 'lodash/isNil';
import { ScaleLinear } from 'd3-scale';
import { AnySelection } from '@d3fc/d3fc-series';

export function getY(
    yScale: ScaleLinear<number, number>,
    svgHeight: number,
    objectHeight: number,
    value?: number
): number | undefined {
    if (isNil(value)) {
        return undefined;
    }

    const [yStart, yEnd] = yScale.domain();
    if (value < yStart || value > yEnd) {
        return undefined;
    }

    const yRange = yEnd - yStart;
    const yMult = svgHeight / yRange;

    const valueOffset = value - yStart;

    const valueOffsetY = valueOffset * yMult;

    const rectY = svgHeight - valueOffsetY - objectHeight / 2;

    return ensureInSvg(svgHeight, objectHeight, rectY);
}

/*
 * Returns the Y values for multiple objects in an SVG, ensuring no overlap
 */
type YValueMap = { [key: string]: number | undefined };
export function adjustYsForOverlap(
    valueMap: YValueMap,
    svgHeight: number,
    objectHeight: number
): YValueMap {
    const values = Object.keys(valueMap).map((key) => ({
        key,
        value: valueMap[key]!,
    }));
    if (values.length <= 1) {
        return valueMap;
    }

    const sortedValues = values.sort((a, b) => a.value - b.value);

    const results = [];
    for (let i = 0; i < sortedValues.length; ++i) {
        const currentItem = sortedValues[i];

        const overlaps = [];
        for (let j = i + 1; j < sortedValues.length; ++j) {
            const nextItem = sortedValues[j];

            const overlappingItemsCombinedLength =
                (overlaps.length + 1) * objectHeight;

            const overlapEndValue =
                overlaps.length > 0
                    ? computeOverlapEndPoint(
                          computeOverlapStartPoint(
                              currentItem,
                              overlaps,
                              overlappingItemsCombinedLength,
                              objectHeight
                          ),
                          overlappingItemsCombinedLength,
                          svgHeight
                      )
                    : currentItem.value + objectHeight;

            if (nextItem.value < overlapEndValue) {
                overlaps.push(nextItem);
                i = j; // jump ahead as required
            } else {
                break;
            }
        }

        if (overlaps.length > 0) {
            const overlappingItemsCombinedLength =
                (overlaps.length + 1) * objectHeight;

            const topAdjustedStartPoint = computeOverlapStartPoint(
                currentItem,
                overlaps,
                overlappingItemsCombinedLength,
                objectHeight
            );
            const bottomAdjustedEndPoint = computeOverlapEndPoint(
                topAdjustedStartPoint,
                overlappingItemsCombinedLength,
                svgHeight
            );

            const startValue =
                bottomAdjustedEndPoint <
                topAdjustedStartPoint + overlappingItemsCombinedLength
                    ? bottomAdjustedEndPoint - overlappingItemsCombinedLength
                    : topAdjustedStartPoint;

            results.push({ ...currentItem, value: startValue });
            overlaps.forEach((x, index) => {
                results.push({
                    ...x,
                    value: startValue + (index + 1) * objectHeight,
                });
            });
        } else {
            results.push(currentItem);
        }
    }

    return results.reduce((acc, val) => {
        acc[val.key] = val.value;
        return acc;
    }, {} as YValueMap);
}

function computeOverlapStartPoint(
    startPoint: { key: string; value: number },
    overlaps: { key: string; value: number }[],
    overlappingItemsCombinedLength: number,
    objectHeight: number
) {
    const lastOverlap = overlaps[overlaps.length - 1];
    const avgMidPoint =
        (startPoint.value + lastOverlap.value + objectHeight) / 2;

    const start = avgMidPoint - overlappingItemsCombinedLength / 2;
    return Math.max(0, start);
}

function computeOverlapEndPoint(
    topAdjustedStartPoint: number,
    overlappingItemsCombinedLength: number,
    svgHeight: number
) {
    const endPoint = topAdjustedStartPoint + overlappingItemsCombinedLength;
    return Math.min(svgHeight, endPoint);
}

function ensureInSvg(
    svgHeight: number,
    objectHeight: number,
    objectY: number
): number {
    if (objectY < 0) {
        return 0;
    }
    return Math.min(objectY, svgHeight - objectHeight);
}

export function getNearestParentElementWithClass(
    selection: AnySelection<SVGElement>,
    className: string
): Element | null {
    let parentNode = selection.node()?.parentElement;

    while (parentNode) {
        if (parentNode.classList.contains(className)) {
            return parentNode;
        }
        parentNode = parentNode.parentElement;
    }

    return null;
}

export function getTranslate(
    node: SVGElement
): { x: number; y: number } | undefined {
    const transform = node.attributes.getNamedItem('transform');
    if (!transform) {
        return undefined;
    }
    if (!transform.value.startsWith('translate')) {
        return undefined;
    }
    const result = transform.value.match(/\((.*?)\)/g);

    if (!result || result.length !== 1) {
        return undefined;
    }

    const values = result[0].replace('(', '').replace(')', '').split(',');

    return { x: parseInt(values[0]), y: parseInt(values[1]) };
}

export function getViewportWidth(selection: AnySelection<SVGElement>) {
    const node = selection.node();
    if (!node) {
        return undefined;
    }
    const viewport = node.viewportElement as SVGSVGElement;
    return viewport.viewBox.baseVal.width;
}
